export const ROLES = [
  'employee',
  'supervisor',
  'park_leadership',
  'ticket_taker',
  'parking',
  'admin',
  'corporate',
];

/** Roles park/company admins may assign (not platform corporate) */
export const COMPANY_ROLES = [
  'employee',
  'supervisor',
  'park_leadership',
  'ticket_taker',
  'parking',
  'admin',
];

export const SUPERVISOR_ASSIGNABLE_ROLES = ['employee', 'supervisor'];

export const PARK_LEADERSHIP_ASSIGNABLE_ROLES = [
  'employee',
  'supervisor',
  'ticket_taker',
  'parking',
];

export const SUBSCRIPTION_PLANS = ['trial', 'professional', 'enterprise'];
export const SUBSCRIPTION_STATUSES = ['active', 'trialing', 'past_due', 'canceled'];

export function parseRole(user) {
  if (!user) return 'employee';
  const rawRole = String(user.role || '').trim().toLowerCase();
  // Explicit ops roles first so they never fall through to employee/search home
  if (rawRole === 'parking') return 'parking';
  if (rawRole === 'ticket_taker') return 'ticket_taker';
  if (rawRole === 'corporate') return 'corporate';
  if (ROLES.includes(rawRole)) {
    return rawRole;
  }
  if (user.supervisor === true || String(user.supervisor).toLowerCase() === 'true') {
    return 'supervisor';
  }
  return 'employee';
}

export function isEmployee(user) {
  return parseRole(user) === 'employee';
}

export function isCorporate(user) {
  return parseRole(user) === 'corporate';
}

export function isAdmin(user) {
  return parseRole(user) === 'admin';
}

export function isSupervisor(user) {
  return parseRole(user) === 'supervisor';
}

export function isParkLeadership(user) {
  return parseRole(user) === 'park_leadership';
}

export function isTicketTaker(user) {
  return parseRole(user) === 'ticket_taker';
}

export function isParking(user) {
  return parseRole(user) === 'parking';
}

/** Single-page ops roles (gate / parking) */
export function isOpsScanner(user) {
  return isTicketTaker(user) || isParking(user);
}

/** Supervisor-equivalent park ops (supervisor + park leadership) */
export function isSupervisorLevel(user) {
  return isSupervisor(user) || isParkLeadership(user);
}

/** Supervisors/leadership may manage staff below admin — never admin or corporate. */
export function canManageUserProfile(actor, target) {
  if (!actor || !target) return false;
  if (isAdmin(actor) || isCorporate(actor)) return true;
  const targetRole = parseRole(target);
  if (targetRole === 'admin' || targetRole === 'corporate') return false;
  if (isParkLeadership(actor)) {
    return ['employee', 'supervisor', 'park_leadership', 'ticket_taker', 'parking'].includes(
      targetRole
    );
  }
  if (isSupervisor(actor)) {
    return targetRole === 'employee' || targetRole === 'supervisor';
  }
  return false;
}

export function canAssignRole(actor, role) {
  if (!actor) return false;
  if (isAdmin(actor) || isCorporate(actor)) {
    return COMPANY_ROLES.includes(role);
  }
  if (isParkLeadership(actor)) {
    return PARK_LEADERSHIP_ASSIGNABLE_ROLES.includes(role);
  }
  if (isSupervisor(actor)) {
    return role === 'employee' || role === 'supervisor';
  }
  return false;
}

/** Default landing page after login */
export function getHomePage(user) {
  if (isCorporate(user)) return 'corporate';
  if (isTicketTaker(user)) return 'gate';
  if (isParking(user)) return 'parking';
  if (usesSearchHome(user)) return 'search';
  return 'dashboard';
}

/** Home is the unified Search screen */
export function usesSearchHome(user) {
  return (
    isEmployee(user) || isSupervisor(user) || isParkLeadership(user) || isAdmin(user)
  );
}

export function canAccessCompensation(user) {
  return isSupervisorLevel(user) || isAdmin(user);
}

/** Full company admin: all users, locations tab */
export function canAccessAdmin(user) {
  return isAdmin(user);
}

/** Supervisors, park leadership, and company admins: user management */
export function canAccessUserManagement(user) {
  return isAdmin(user) || isSupervisorLevel(user);
}

/** Park leadership: edit products for sale */
export function canAccessProducts(user) {
  return isParkLeadership(user) || isAdmin(user);
}

/** Ticket takers: gate scan page only */
export function canAccessGate(user) {
  return isTicketTaker(user) || isAdmin(user) || isParkLeadership(user) || isSupervisor(user);
}

/** Parking attendants: parking entitlement scan page */
export function canAccessParking(user) {
  return isParking(user) || isAdmin(user) || isParkLeadership(user) || isSupervisor(user);
}

/** Platform corporate login: companies + subscriptions */
export function canAccessCorporate(user) {
  return isCorporate(user);
}

export function roleLabel(role) {
  const labels = {
    employee: 'Employee',
    supervisor: 'Supervisor',
    park_leadership: 'Park Leadership',
    ticket_taker: 'Ticket Taker',
    parking: 'Parking',
    admin: 'Admin',
    corporate: 'Corporate',
  };
  return labels[role] || role;
}
